// ABOUTME: Totals panel for above a tab view: a grid of figures beside the same figure over time.
// ABOUTME: Driven by two query hooks, so any page can mount one by pointing it at its endpoints.

import { Box, useTheme } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { endOfMonth, endOfWeek, format, startOfMonth, startOfWeek, subMonths, subWeeks } from "date-fns";

import { tokens } from "../../../theme";
import { MultiChart } from "../charts/HorizontalBarChart";
import ShDateRangePicker from "../ShDateRangePicker";
import Subheading from "../text/Subheading";
import OverflowRow from "../OverflowRow";
import ShPillToggle from "../buttons/ShPillToggle";
import StatCard from "./StatCard";
import StatCardRow from "./StatCardRow";
import { formatNumber, formatPrice } from "../../../utils/helpers";

// A month to a bar reads a year at a glance; a week to a bar is for looking at a quarter up
// close. Each bucket size opens on the span it is worth reading over, so switching between them
// moves the period along with it.
const GRANULARITIES = ["month", "week"];
const GRANULARITY_OPTIONS = GRANULARITIES.map((granularity) => ({ value: granularity, label: granularity }));

const getDefaultChartRange = (granularity) => {
    const now = new Date();

    // Half a year of weeks: enough to read a season out of, while still narrow enough that every
    // bar keeps a label.
    if (granularity === "week") {
        return {
            startDate: format(startOfWeek(subWeeks(now, 25), { weekStartsOn: 1 }), 'yyyy-MM-dd'),
            endDate: format(endOfWeek(now, { weekStartsOn: 1 }), 'yyyy-MM-dd')
        };
    }

    // Sixteen months rather than twelve, so the months just gone sit on the same chart as the
    // same months a year earlier and can be read straight off against them.
    return {
        startDate: format(startOfMonth(subMonths(now, 15)), 'yyyy-MM-dd'),
        endDate: format(endOfMonth(now), 'yyyy-MM-dd')
    };
};

/**
 * ShTotalsPanel
 *
 * The panel that sits above a tab view: what the listed rows add up to, beside the same figure
 * plotted over time. Totals and chart each carry their own date picker, so a number and a trend
 * can be read over different periods, and the chart switches between a bar a month and a bar a
 * week.
 *
 * It owns the periods and asks for the data itself, through two hooks the page hands it:
 *
 * - useTotals({ ...filter, startDate, endDate })                -> { totals: { [key]: number } }
 * - useChart({ ...filter, startDate, endDate, granularity })    -> { data: { categories, series } }
 *
 * `filter` is whatever else narrows the figures — a relationId on a relation's own page — merged
 * into both requests, so the same panel serves a warehouse-wide overview and a single relation.
 *
 * Both are RTK Query hooks, so a page adds a panel by writing those two endpoints and listing
 * the figures it wants shown:
 *
 *   cards = [{ key: "totalRevenue", label: t("revenue"), icon: <EuroIcon className="logo"/>,
 *              color: "orange", type: "currency" }]
 *
 * `key` names the field to read out of `totals`; `type` is "currency", "number" or left out for
 * a value that is already a string. `color` takes any hue of the base palette (see StatCard).
 *
 * A series name coming back from the endpoint is a translation key, so the page names its own
 * figures. `chartSeries` says how to read each one, by position:
 *
 *   chartSeries = [{ type: "currency" }, { type: "number", chartType: "hidden" }]
 *
 * The first series is drawn as bars and every one after it as a line, on its own hidden scale,
 * so a count beside an amount of money does not squash whichever runs smaller. A series set to
 * "hidden" is read in the tooltip but never drawn, for a figure worth showing beside the bars
 * without a line cutting across them.
 */
const ShTotalsPanel = ({
    useTotals,
    useChart,
    cards,
    title,
    chartTitle,
    chartSubtitles,
    columns = 2,
    chartColor,
    chartSeries,
    filter,
    defaultGranularity = "month"
}) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // The running month, the period an overview opens on.
    const now = new Date();
    const [totalsDates, setTotalsDates] = useState({
        startDate: format(startOfMonth(now), 'yyyy-MM-dd'),
        endDate: format(endOfMonth(now), 'yyyy-MM-dd')
    });

    const [granularity, setGranularity] = useState(GRANULARITIES.includes(defaultGranularity) ? defaultGranularity : "month");
    const [chartDates, setChartDates] = useState(getDefaultChartRange(defaultGranularity));

    // Switching bucket size moves the period with it: a year of weeks is unreadable, and a
    // quarter of months is four bars.
    const changeGranularity = (next) => {
        if (next === granularity) {
            return;
        }
        setGranularity(next);
        setChartDates(getDefaultChartRange(next));
    };

    const { data: totalsData, isLoading, isFetching } = useTotals({ ...filter, ...totalsDates });
    const totals = totalsData?.totals;
    const totalsLoading = isLoading || isFetching;

    const {
        data: chartData,
        isLoading: chartIsLoading,
        isFetching: chartIsFetching,
        isError: chartIsError,
        refetch: chartRefetch
    } = useChart({ ...filter, ...chartDates, granularity });

    const formatBy = (type) => type === "currency"
        ? (value) => formatPrice(i18n, value)
        : (value) => formatNumber(i18n, value);

    // The endpoint names its series with translation keys, so the tooltip and the legend read in
    // the reader's language rather than in the field name the query happened to use.
    const chartValues = useMemo(() => (chartData?.data?.series ?? []).map((serie, index) => {
        const chartType = chartSeries?.[index]?.chartType ?? (index === 0 ? "bar" : "line");

        return {
            ...serie,
            name: t(serie.name),
            type: chartType === "hidden" ? "line" : chartType,
            ...(chartType === "hidden" ? { strokeWidth: 0 } : {})
        };
    }), [chartData, chartSeries, t]);

    const chartFormatters = useMemo(
        () => (chartData?.data?.series ?? []).map((serie, index) => formatBy(chartSeries?.[index]?.type)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [chartData, chartSeries, i18n.language]
    );

    const displayValue = (card) => {
        const value = totals?.[card.key] ?? 0;

        switch (card.type) {
            case "currency":
                return formatPrice(i18n, value);
            case "number":
                return formatNumber(i18n, value);
            default:
                return value;
        }
    };

    return (
        <OverflowRow className={"is-mobile-col"}>
            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                    flex: 2,
                    boxShadow: '0px 1px 2px 1px rgba(49, 65, 95, 0.15)',
                    borderRadius: 4,
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Box sx={{ display: "flex", alignItems: 'center', justifyContent: "space-between" }}>
                    <Subheading>{title ?? t("totals")}</Subheading>
                    <Box>
                        <ShDateRangePicker
                            startDate={totalsDates.startDate}
                            endDate={totalsDates.endDate}
                            size={32}
                            onClose={(value) => {
                                setTotalsDates({
                                    startDate: value[0].formattedStartDate,
                                    endDate: value[0].formattedEndDate
                                })
                            }}
                        />
                    </Box>
                </Box>

                <StatCardRow columns={columns} sx={{ mt: 3, flex: 1, gridAutoRows: "minmax(0, 1fr)" }}>
                    {cards.map((card) => (
                        <StatCard
                            filled
                            key={card.key}
                            label={card.label}
                            value={displayValue(card)}
                            icon={card.icon}
                            color={card.color}
                            isLoading={totalsLoading}
                        />
                    ))}
                </StatCardRow>
            </Box>

            <Box
                sx={{
                    flex: 3,
                    "@media screen and (max-width: 47.9375em)": {
                        minWidth: "calc(100% - 48px)"
                    }
                }}
            >
                <MultiChart
                    height={"100%"}
                    optionsEnabled={false}
                    onRetry={chartRefetch}
                    isError={chartIsError}
                    isLoading={chartIsLoading || chartIsFetching}
                    title={chartTitle ?? t("through_time")}
                    subtitle={chartSubtitles?.[granularity]}
                    type="bar"
                    staticColors={[chartColor ?? colors.primary[400]]}
                    colors={['primary']}
                    categories={chartData?.data?.categories ?? []}
                    values={chartValues}
                    valueFormatters={chartFormatters}
                    dataLabelAngle={-65}
                    secondaryAxis
                    color={"default"}
                    tickAmount={8}
                    filter={
                        <>
                            <ShPillToggle
                                options={GRANULARITY_OPTIONS}
                                value={granularity}
                                onChange={changeGranularity}
                            />
                            <ShDateRangePicker
                                startDate={chartDates.startDate}
                                endDate={chartDates.endDate}
                                size={32}
                                onClose={(value) => {
                                    setChartDates({
                                        startDate: value[0].formattedStartDate,
                                        endDate: value[0].formattedEndDate
                                    })
                                }}
                            />
                        </>
                    }
                />
            </Box>
        </OverflowRow>
    );
};

export default ShTotalsPanel;
